import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Tema = 'claro' | 'escuro' | 'automatico';
export type Fonte = 'pequena' | 'media' | 'grande';
export type Densidade = 'compacta' | 'normal' | 'confortavel';
export type Cantos = 'reto' | 'medio' | 'redondo';

export const CHAVE_APARENCIA = 'social.aparencia';

/** Matiz da marca Specializer (azul #2478fc do logotipo). Serve de padrão e de "voltar". */
export const MATIZ_PADRAO = 260;

export const FONTE_PX: Record<Fonte, string> = {
  pequena: '14px',
  media: '15px',
  grande: '17px',
};

export const CANTOS_REM: Record<Cantos, string> = {
  reto: '0.25rem',
  medio: '0.625rem',
  redondo: '1rem',
};

interface EstadoAparencia {
  tema: Tema;
  matiz: number;
  fonte: Fonte;
  densidade: Densidade;
  cantos: Cantos;
  altoContraste: boolean;
  menosMovimento: boolean;
  /** Barra lateral recolhida no formato de trilho (só ícones). */
  trilho: boolean;
  /**
   * Barra lateral escondida por completo no desktop.
   *
   * Estado separado do trilho, e não um terceiro valor de um mesmo campo:
   * quem esconde para ler uma tabela larga quer a lateral de VOLTA como
   * estava — trilho se estava em trilho, inteira se estava inteira.
   */
  oculta: boolean;
  /**
   * Grupos do menu recolhidos, pelo TÍTULO.
   *
   * Guarda o que está fechado, e não o que está aberto: grupo novo — e eles
   * aparecem a cada módulo — nasce ABERTO. Guardando os abertos, todo módulo
   * novo chegaria escondido e ninguém descobriria a tela que acabou de existir.
   */
  gruposFechados: string[];

  definirTema: (tema: Tema) => void;
  definir: (parcial: Partial<Omit<EstadoAparencia, 'definir' | 'definirTema'>>) => void;
  alternarTrilho: () => void;
  alternarOculta: () => void;
  alternarGrupo: (titulo: string) => void;
  /**
   * Restaura só a APARÊNCIA ao padrão de fábrica.
   *
   * Nunca usar `localStorage.clear()` para isto: apagaria `social.refresh`
   * junto e deslogaria quem só queria voltar a fonte ao normal.
   */
  restaurar: () => void;
}

const PADRAO = {
  tema: 'automatico' as Tema,
  matiz: MATIZ_PADRAO,
  fonte: 'media' as Fonte,
  densidade: 'normal' as Densidade,
  cantos: 'medio' as Cantos,
  altoContraste: false,
  menosMovimento: false,
  trilho: false,
  oculta: false,
  gruposFechados: [] as string[],
};

/**
 * Preferência de aparência.
 *
 * Vai além do claro/escuro porque o sistema é usado em contextos diferentes: a
 * mesma tela é lida no monitor do escritório e no tablet ao lado da mesa de
 * conferência, onde fonte maior e alvo maior fazem diferença real.
 *
 * `automatico` segue o sistema operacional e REAGE quando ele muda no meio do
 * uso. Escolha explícita vence o sistema.
 */
export const useAparencia = create<EstadoAparencia>()(
  persist(
    (set, get) => ({
      ...PADRAO,
      definirTema: (tema) => set({ tema }),
      definir: (parcial) => set(parcial),
      alternarTrilho: () => set({ trilho: !get().trilho }),
      alternarOculta: () => set({ oculta: !get().oculta }),
      alternarGrupo: (titulo) => {
        const fechados = get().gruposFechados;
        set({
          gruposFechados: fechados.includes(titulo)
            ? fechados.filter((t) => t !== titulo)
            : [...fechados, titulo],
        });
      },
      restaurar: () => set(PADRAO),
    }),
    { name: CHAVE_APARENCIA },
  ),
);

/** O tema pedido resulta em escuro agora? */
export function estaEscuro(tema: Tema): boolean {
  if (tema === 'escuro') return true;
  if (tema === 'claro') return false;
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );
}

/**
 * Aplica tudo no `<html>` suprimindo transição por um frame.
 *
 * Sem a supressão, todo elemento com `transition-colors` anima a troca e a
 * tela inteira "lava" de claro para escuro.
 */
export function aplicarAparencia(estado: {
  tema: Tema;
  matiz: number;
  fonte: Fonte;
  densidade: Densidade;
  cantos: Cantos;
  altoContraste: boolean;
  menosMovimento: boolean;
}): void {
  const raiz = document.documentElement;

  raiz.style.setProperty('--pri-h', String(estado.matiz));
  raiz.style.setProperty('--app-fs', FONTE_PX[estado.fonte]);
  raiz.style.setProperty('--raio', CANTOS_REM[estado.cantos]);
  raiz.dataset['densidade'] = estado.densidade;
  raiz.dataset['contraste'] = estado.altoContraste ? 'alto' : 'normal';
  raiz.dataset['movimento'] = estado.menosMovimento ? 'reduzido' : 'normal';

  raiz.classList.add('theme-changing');
  raiz.classList.toggle('dark', estaEscuro(estado.tema));
  void raiz.offsetHeight;
  window.requestAnimationFrame(() => raiz.classList.remove('theme-changing'));
}
